import { useCallback, useEffect, useState } from 'react'
import { annualArchiveApi, type AnnualArchiveDownload, type AnnualArchiveYear } from '../api/annualArchive'
import { RETENTION_CATEGORY_LABELS } from '../api/retention'
import { Button, Callout, Card, CardContent, CardHeader, DataTable, EmptyState, LoadingState, Page, PageHeader, StatusPill } from '../components/ui'

/**
 * R5-06 (D-13b): archivio annuale dei dati oltre i tempi di conservazione. Niente viene cancellato: per ogni anno si
 * genera (in background) un pacchetto ZIP con CSV e JSON per tabella, indice e impronte SHA-256, da scaricare e
 * custodire su supporto fisico in azienda. Ogni download e' registrato. Attivo solo con il flag «privacy.archivio_annuale».
 */

function errMsg(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback
}

function size(n: number | null): string {
  if (n === null) return '—'
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

const when = (s: string | null) => (s ? new Date(s).toLocaleString('it-IT') : '—')

const STATUS: Record<string, { label: string; tone: 'neutral' | 'primary' | 'success' | 'error' }> = {
  in_coda: { label: 'In coda', tone: 'neutral' },
  in_corso: { label: 'In preparazione', tone: 'primary' },
  pronto: { label: 'Pronto', tone: 'success' },
  errore: { label: 'Errore', tone: 'error' },
}

export function AnnualArchivePage() {
  const [years, setYears] = useState<AnnualArchiveYear[] | null>(null)
  const [downloads, setDownloads] = useState<AnnualArchiveDownload[]>([])
  const [error, setError] = useState('')
  const [busyYear, setBusyYear] = useState<number | null>(null)

  const load = useCallback(async () => {
    try {
      const [y, d] = await Promise.all([annualArchiveApi.list(), annualArchiveApi.downloads()])
      setYears(y.items)
      setDownloads(d.items)
      setError('')
    } catch (e) {
      setError(errMsg(e, 'Impossibile caricare l\'archivio annuale (flag spento o utente non autorizzato).'))
      setYears(prev => prev ?? [])
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const working = years?.some(y => y.archive?.status === 'in_coda' || y.archive?.status === 'in_corso') ?? false
  useEffect(() => {
    if (!working) return
    const t = window.setInterval(() => void load(), 4000)
    return () => window.clearInterval(t)
  }, [working, load])

  async function generate(year: number) {
    setBusyYear(year)
    try {
      await annualArchiveApi.generate(year)
      await load()
    } catch (e) {
      setError(errMsg(e, 'Generazione non riuscita.'))
    } finally {
      setBusyYear(null)
    }
  }

  return (
    <Page>
      <PageHeader title="Archivio annuale" icon="inventory_2" />
      <div className="d-flex flex-column gap-3">
        <Callout tone="info" role="note">
          Niente viene cancellato dal database. Per ogni anno con dati oltre i tempi di conservazione (lead 24 mesi, registri
          12 mesi, documenti 10 anni, secondo le politiche impostate) puoi preparare un pacchetto ZIP con CSV e JSON per tabella,
          l'indice e le impronte SHA-256 (file SHA256SUMS.txt, verificabile con <code>sha256sum -c</code>). Scaricalo e custodiscilo
          su supporto fisico. Dei documenti il pacchetto contiene i soli metadati: i file veri restano nel deposito dei documenti.
        </Callout>
        {error && <Callout tone="error">{error}</Callout>}
        {years === null ? <LoadingState /> : years.length === 0 && !error ? (
          <EmptyState icon="inventory_2" title="Nessun dato oltre i tempi di conservazione" />
        ) : (
          <Card variant="outlined" padding="none">
            <CardHeader title="Anni" icon="calendar_month" />
            <DataTable variant="plain">
              <thead><tr><th>Anno</th><th>Righe oltre la durata</th><th>Stato</th><th>Pacchetto</th><th /></tr></thead>
              <tbody>
                {years.map(y => {
                  const a = y.archive
                  const st = a ? STATUS[a.status] : undefined
                  const pending = a?.status === 'in_coda' || a?.status === 'in_corso'
                  return (
                    <tr key={y.year}>
                      <td><strong>{y.year}</strong></td>
                      <td>
                        {y.totalRows.toLocaleString('it-IT')}
                        <div className="text-muted md-typescale-body-small">
                          {y.categories.map(c => `${RETENTION_CATEGORY_LABELS[c.category] ?? c.category}: ${c.rows.toLocaleString('it-IT')}`).join(' · ')}
                        </div>
                      </td>
                      <td>
                        {st ? <StatusPill tone={st.tone}>{st.label}</StatusPill> : <StatusPill tone="neutral">Da preparare</StatusPill>}
                        {a?.status === 'errore' && a.error && <div className="text-muted md-typescale-body-small">{a.error}</div>}
                      </td>
                      <td className="md-typescale-body-small">
                        {a?.status === 'pronto' ? (
                          <>
                            {a.fileName} · {size(a.sizeBytes)} · {(a.totalRows ?? 0).toLocaleString('it-IT')} righe<br />
                            Generato il {when(a.generatedAt)}<br />
                            SHA-256 <code>{a.sha256}</code>
                          </>
                        ) : '—'}
                      </td>
                      <td>
                        <Button variant="filled" size="sm" disabled={pending || busyYear === y.year || y.totalRows === 0} onClick={() => void generate(y.year)}>
                          {a?.status === 'pronto' ? 'Rigenera' : 'Prepara pacchetto'}
                        </Button>
                        {a?.status === 'pronto' && (
                          <a className="ms-2" href={annualArchiveApi.downloadUrl(y.year)}>Scarica</a>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </DataTable>
          </Card>
        )}
        <Card variant="outlined" padding="none">
          <CardHeader title="Chi ha scaricato" icon="history" />
          <CardContent>
            {downloads.length === 0 ? <p className="text-muted md-typescale-body-small">Nessun download registrato.</p> : (
              <DataTable variant="plain">
                <thead><tr><th>Quando</th><th>Anno</th><th>Utente</th><th>SHA-256</th></tr></thead>
                <tbody>
                  {downloads.map(d => (
                    <tr key={d.id}>
                      <td>{when(d.downloadedAt)}</td>
                      <td>{d.year}</td>
                      <td>{d.downloadedByName ?? (d.downloadedBy !== null ? `Utente ${d.downloadedBy}` : '—')}</td>
                      <td className="md-typescale-body-small"><code>{d.sha256 ?? '—'}</code></td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            )}
          </CardContent>
        </Card>
      </div>
    </Page>
  )
}
